import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Radar, LogOut, ChevronDown, Lock, User, LayoutDashboard, Inbox } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import ThemeToggle from "./ThemeToggle";
import ChangePasswordModal from "./ChangePasswordModal";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const roleBadge = {
  admin: { label: "Admin Console", cls: "bg-indigo-50 text-indigo-700", color: "#2f2b8f" },
  employee: { label: "Request Center", cls: "bg-indigo-50 text-indigo-700", color: "#2f2b8f" },
  client: { label: "Client Workspace", cls: "bg-emerald-50 text-emerald-700", color: "#059669" },
};

const PortalNav = ({ title }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPw, setShowPw] = useState(false);
  const badge = roleBadge[user?.role] || roleBadge.admin;
  const isAdmin = user?.role === "admin";
  const onRequestCenter = location.pathname.startsWith("/employee");

  const handleLogout = () => { logout(); navigate("/"); };
  const initials = (user?.name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

  return (
    <header className="sticky top-0 z-40 border-b border-slate-100 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/")} className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: badge.color }}>
              <Radar className="h-5 w-5 text-white" strokeWidth={2} />
            </div>
            <span className="text-lg font-bold text-[#1a1a2e] dark:text-slate-100">Files on Radar</span>
          </button>
          {title && <span className={`ml-1 rounded-full px-3 py-1 text-xs font-semibold ${badge.cls} dark:bg-indigo-500/15 dark:text-indigo-300`}>{title}</span>}
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <div data-testid="portal-switcher" className="hidden items-center rounded-xl border border-slate-200 bg-slate-50 p-0.5 dark:border-slate-700 dark:bg-slate-800 sm:inline-flex">
              <button data-testid="switch-admin" onClick={() => navigate("/dashboard")} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${!onRequestCenter ? "bg-[#2f2b8f] text-white shadow-sm" : "text-slate-500 hover:text-slate-700 dark:text-slate-400"}`}>
                <LayoutDashboard className="h-3.5 w-3.5" /> Admin
              </button>
              <button data-testid="switch-requestcenter" onClick={() => navigate("/employee")} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${onRequestCenter ? "bg-[#2f2b8f] text-white shadow-sm" : "text-slate-500 hover:text-slate-700 dark:text-slate-400"}`}>
                <Inbox className="h-3.5 w-3.5" /> Request Center
              </button>
            </div>
          )}
          <ThemeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white py-1.5 pl-1.5 pr-2.5 transition-colors hover:bg-slate-50">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold text-white" style={{ backgroundColor: badge.color }}>{initials}</span>
                <span className="hidden text-left sm:block">
                  <span className="block text-sm font-semibold leading-tight text-[#1a1a2e]">{user?.name}</span>
                  <span className="block text-[11px] leading-tight text-slate-400">{user?.firm_name}</span>
                </span>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-xl">
              <DropdownMenuLabel className="flex items-center gap-2 text-slate-500">
                <User className="h-4 w-4" /> <span className="truncate">{user?.email}</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowPw(true)} className="cursor-pointer"><Lock className="mr-2 h-4 w-4" /> Change password</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-rose-600 focus:text-rose-600"><LogOut className="mr-2 h-4 w-4" /> Logout</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <ChangePasswordModal open={showPw} onClose={() => setShowPw(false)} />
    </header>
  );
};

export default PortalNav;
